import { GoogleLoginResponse, GoogleLoginResponseOffline } from 'react-google-login';
import logLogInFailed from '@alltrails/amplitude/events/logLogInFailed';
import logSignUpFailed from '@alltrails/amplitude/events/logSignUpFailed';
import { ApiError } from '@alltrails/core/utils/api';
import logError from '@alltrails/core/utils/logError';
import { post } from '@alltrails/core/utils/post';
import LoginResponse from '../types/LoginResponse';
import { authSuccessCallback } from '../utils/AuthHandlers';

function getToken(googleUserOrToken: GoogleLoginResponse | GoogleLoginResponseOffline | string) {
  if (typeof googleUserOrToken === 'string') {
    return googleUserOrToken;
  }

  if ('getAuthResponse' in googleUserOrToken) {
    return googleUserOrToken.getAuthResponse().id_token;
  }

  return undefined;
}

export default function useGoogleHandler() {
  async function googleHandler({
    googleUserOrToken,
    handlerType,
    referralLink,
    oneTap,
    grantType
  }: {
    googleUserOrToken: GoogleLoginResponse | GoogleLoginResponseOffline | string;
    handlerType: 'login' | 'signup';
    referralLink?: string;
    oneTap?: boolean;
    grantType?: string | null;
  }) {
    const token = getToken(googleUserOrToken);

    let response: LoginResponse;

    try {
      response = await post<LoginResponse>('/identity/auth/google', {
        client_id: process.env.NEXT_PUBLIC_IDENTITY_CLIENT_ID,
        google_token: token,
        ...(referralLink && { referral_code: referralLink }),
        ...(oneTap && { one_tap: oneTap }),
        ...(grantType && { grant_type: grantType })
      });

      authSuccessCallback(response, oneTap ? 'google one tap' : 'google', response.is_collab_list_invite);
    } catch (e: any) {
      const failureType = e instanceof ApiError && e.data?.errors?.length > 0 ? e.data.errors[0].code : e.status;

      if (handlerType === 'signup') {
        logSignUpFailed({ authentication_type: oneTap ? 'google one tap' : 'google', failure_type: failureType });
      } else {
        logLogInFailed({
          authentication_type: oneTap ? 'google one tap' : 'google',
          failure_type: failureType
        });
      }

      logError(new Error('There was an error logging in to or signing up with Google', { cause: e }));
      throw e;
    }

    return response;
  }

  return googleHandler;
}
